import {
  Box,
  Button,
  Flex,
  Icon,
  Spacer,
  Tab,
  TabList,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { BigNumber } from '@ethersproject/bignumber';
import { Card } from '@ui/components/Card';
import type { ClaimedVnoSimpleLineChartProps } from '@ui/components/ClaimedVnoSimpleLineChart';
import { ClaimedVnoSimpleLineChart } from '@ui/components/ClaimedVnoSimpleLineChart';
import { features } from '@ui/components/Features';
import HowItWorksDrawer from '@ui/components/HowItWorksDrawer';
import { LazyComponent } from '@ui/components/LazyComponent';
import { useDefaultAppBar } from '@ui/components/MobileAppBarProvider';
import { useIsDesktop } from '@ui/components/MobileOrDesktop';
import { VaultsPieChart } from '@ui/components/VaultsPieChart';
import {
  useVenoFountainData,
  useVenoReservoirData,
} from '@ui/components/VenoInfoView/useVenoInfoData';
import { VaultsInfo } from '@ui/components/VenoInfoView/VaultsInfo';
import WithWallet from '@ui/components/Wallet/WithWallet';
import { getPathInfo, useRealPathname } from '@ui/hooks/router';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { HintType } from '@ui/hooks/useShowHowItWorks';
import useVenoEarnings from '@ui/hooks/useVenoEarnings';
import {
  useVenoFountainHarvest,
  useVenoReservoirHarvest,
} from '@ui/hooks/useVenoHarvest';
import { useTranslations } from '@ui/i18n';
import { useRouter } from '@ui/overrides/next/router';
import { MAX_POOL_ID, MAXW_DESKTOP_CTA, PRELAUNCH } from '@ui/utils/constants';
import { formatLockPeriod } from '@ui/utils/formatLockPeriod';
import getBgImage from '@ui/utils/getBgImageProps';
import { ArrowBack } from '@veno-app/icons';
import { currentWallet } from '@veno-app/wallet';
import type { NextPage } from 'next';
import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useMount } from 'react-use';

import { Garden } from '../Garden';
import LockVnoDrawer, { useLockVnoDrawerStore } from '../Garden/LockVnoDrawer';
import { useGardenModalStore } from '../Garden/useGardenModalStore';
import { useHowItWorksData } from './constants';
import EarnWithVno from './EarnWithVno';
import ReservoirOrFountain, {
  DepositDrawer,
  useDepositDrawer,
  useVaultDetailDrawer,
  useVaultSelectionStore,
  VaultDetail,
} from './ReservoirAndFountain';
import { CollectPenaltyCard } from './ReservoirAndFountain/CollectPenaltyCard';
import CollectPenaltyDrawer, {
  useCollectPenaltyDrawerStore,
} from './ReservoirAndFountain/CollectPenaltyDrawer';
import DepositUpgradeDrawer, {
  useDepositUpgradeDrawer,
} from './ReservoirAndFountain/DepositUpgradeDrawer';
import {
  StakeCardsDrawer,
  useStakeCardsDrawer,
} from './ReservoirAndFountain/StakeCards';
import {
  useVaultWithdrawDrawerStore,
  VaultWithdrawDrawer,
} from './VaultWithdrawDrawer';

const bgImageProps = getBgImage('PATTERN_3');
const { useIsZksyncChainId } = currentWallet;
const isVnoTokenZksyncEnabled = isFeatureEnabled(features.vnoTokenZksync);

export const VnoAndGarden: NextPage = () => {
  const t = useTranslations();

  const isDesktop = useIsDesktop();
  const selectedVault = useVaultSelectionStore((s) => s.selectedVault);
  const isVaultDetailDrawerOpen = useVaultDetailDrawer((s) => s.isOpen);
  const howItWorksData = useHowItWorksData();

  useVnoAndGarden();

  if (isDesktop && selectedVault) {
    return (
      <>
        <SelectedVaultView />
        <WithVaultDrawers />
      </>
    );
  }

  if (isDesktop && isVaultDetailDrawerOpen) {
    return (
      <>
        <VaultPoolView />
        <WithVaultDrawers />
      </>
    );
  }

  return (
    <>
      <WithVaultDrawers />
      <Flex position="relative" align="stretch" flexDirection="column">
        <HowItWorksDrawer hintType={HintType.VNO} {...howItWorksData} />
        <Box minH="400px" {...bgImageProps} bgPos={'right top -100px'}>
          <WithWallet
            flex={1}
            actionHint={t('earn additional rewards')}
            supportedCoinModes={
              isVnoTokenZksyncEnabled ? undefined : ['cro', 'atom']
            }
            py={4}
            px={0}
          >
            <EarnWithVno />
            <Spacer
              my={{
                base: 12,
                desktop: '48px',
              }}
            />
            <Garden />
          </WithWallet>
        </Box>
      </Flex>
    </>
  );
};

const WithVaultDrawers: FC = () => {
  const isCollectPenaltyDrawerOpen = useCollectPenaltyDrawerStore(
    (s) => s.isOpen,
  );
  const isLockVnoDrawerOpen = useLockVnoDrawerStore((s) => s.isOpen);
  const isDepositDrawerOpen = useDepositDrawer((s) => s.isOpen);
  const isVaultWithdrawDrawerOpen = useVaultWithdrawDrawerStore(
    (s) => s.isOpen,
  );
  const isStakeCardsDrawerOpen = useStakeCardsDrawer((s) => s.isOpen);
  const isDepositUpgradeDrawerOpen = useDepositUpgradeDrawer((s) => s.isOpen);

  return (
    <>
      <LazyComponent isOpen={isCollectPenaltyDrawerOpen}>
        <CollectPenaltyDrawer />
      </LazyComponent>
      <LazyComponent isOpen={isLockVnoDrawerOpen}>
        <LockVnoDrawer />
      </LazyComponent>
      <LazyComponent isOpen={isDepositDrawerOpen}>
        <DepositDrawer />
      </LazyComponent>
      <LazyComponent isOpen={isVaultWithdrawDrawerOpen}>
        <VaultWithdrawDrawer />
      </LazyComponent>
      <LazyComponent isOpen={isStakeCardsDrawerOpen}>
        <StakeCardsDrawer />
      </LazyComponent>
      <LazyComponent isOpen={isDepositUpgradeDrawerOpen}>
        <DepositUpgradeDrawer />
      </LazyComponent>
    </>
  );
};

const VaultPoolView = () => {
  const t = useTranslations();
  const vaultType = useVaultDetailDrawer((s) => s.type);
  const openVaultDetailDrawer = useVaultDetailDrawer((s) => s.onOpen);
  const onVaultDetailDrawerClose = useVaultDetailDrawer((s) => s.onClose);

  const isZksync = useIsZksyncChainId();
  const venoReservoirData = useVenoReservoirData(!PRELAUNCH);
  const venoFountainData = useVenoFountainData(!PRELAUNCH);

  const {
    mutate: venoReservoirHarvest,
    isLoading: isVenoReservoirHarvestLoading,
  } = useVenoReservoirHarvest();

  const {
    mutate: venoFountainHarvest,
    isLoading: isVenoFountainHarvestLoading,
  } = useVenoFountainHarvest();

  const { historicalData } = useVenoEarnings('FOUNTAIN');
  const claimedVnoList: ClaimedVnoSimpleLineChartProps['dataList'] = useMemo(
    () =>
      historicalData
        ? historicalData.map((d) => {
            return {
              claimedVno: d.valueBigNumber,
              date: d.date.getTime(),
            };
          })
        : [],
    [historicalData],
  );

  return (
    <Box position="relative">
      <Button
        variant="link"
        color="text-link"
        position="absolute"
        left="0"
        top="10px"
        leftIcon={<Icon as={ArrowBack} boxSize="15px" />}
        onClick={() => onVaultDetailDrawerClose()}
      >
        {t('Back to {page}', {
          page: 'VNO',
        })}
      </Button>
      <Tabs
        hidden={isZksync && !isFeatureEnabled(features.reservoirZksync)}
        defaultIndex={vaultType === 'FOUNTAIN' ? 0 : 1}
        maxW="564px"
        marginX="auto"
      >
        <TabList>
          <Tab onClick={() => openVaultDetailDrawer('FOUNTAIN')}>Fountain</Tab>
          <Tab onClick={() => openVaultDetailDrawer('RESERVOIR')}>
            Reservoir
          </Tab>
        </TabList>
      </Tabs>
      <Flex
        w="full"
        justifyContent="center"
        gap="24px"
        mt={
          isZksync && !isFeatureEnabled(features.reservoirZksync)
            ? '64px'
            : '32px'
        }
      >
        <Card
          position="relative"
          w="50%"
          flex="1"
          pb="32px"
          m={0}
          px={8}
          pt={4}
        >
          <Text textStyle="h3">
            {vaultType === 'RESERVOIR'
              ? 'Reservoir earnings'
              : 'Fountain earnings'}
          </Text>
          {!!claimedVnoList?.length && vaultType === 'FOUNTAIN' && (
            <ClaimedVnoSimpleLineChart dataList={claimedVnoList} mt="20px" />
          )}
          <Text color="text-light" mt="32px" mb="24px">
            {vaultType === 'RESERVOIR'
              ? t(
                  isZksync
                    ? 'Vest VNO for a share of Veno’s rewards, denominated in WETH'
                    : 'Vest VNO for a share of Veno’s rewards, denominated in WCRO and ATOM',
                )
              : t('Vest VNO for more rewards, denominated in VNO')}
          </Text>
          {!isZksync && vaultType === 'FOUNTAIN' && (
            <CollectPenaltyCard type={vaultType} my="20px" />
          )}
          {vaultType === 'RESERVOIR' ? (
            <VaultsInfo vaultType="RESERVOIR" />
          ) : (
            <VaultsInfo vaultType="FOUNTAIN" />
          )}
          {vaultType === 'RESERVOIR' ? (
            <VaultsPieChart
              mt="52px"
              month3={venoReservoirData.vnoAllocations[0]}
              month12={venoReservoirData.vnoAllocations[1]}
              month48={venoReservoirData.vnoAllocations[2]}
              month96={venoReservoirData.vnoAllocations[3]}
            />
          ) : (
            <VaultsPieChart
              mt="52px"
              month3={venoFountainData.vnoAllocations[0]}
              month12={venoFountainData.vnoAllocations[1]}
              month48={venoFountainData.vnoAllocations[2]}
              month96={venoFountainData.vnoAllocations[3]}
            />
          )}
          {vaultType !== 'RESERVOIR' ||
          (
            venoReservoirData.pendingRewardsMap.wcro ??
            venoReservoirData.pendingRewardsMap.weth ??
            BigNumber.from(0)
          ).lte(BigNumber.from(0)) ? null : (
            <Button
              variant="primary"
              isLoading={isVenoReservoirHarvestLoading}
              onClick={() => venoReservoirHarvest()}
            >
              {t('Harvest {pool} rewards', {
                pool: 'Reservoir',
              })}
            </Button>
          )}
          {vaultType !== 'FOUNTAIN' ||
          !venoFountainData.pendingRewards ||
          venoFountainData.pendingRewards.lte(BigNumber.from(0)) ? null : (
            <Button
              variant="primary"
              isLoading={isVenoFountainHarvestLoading}
              onClick={() => venoFountainHarvest()}
            >
              {t('Harvest {pool} rewards', {
                pool: 'Fountain',
              })}
            </Button>
          )}
        </Card>
        <Card
          position="relative"
          w="50%"
          flex="1"
          pb="32px"
          m={0}
          px={8}
          pt={4}
        >
          <ReservoirOrFountain type={vaultType} />
        </Card>
      </Flex>
    </Box>
  );
};

const SelectedVaultView = () => {
  const t = useTranslations();

  const selectedVault = useVaultSelectionStore((s) => s.selectedVault);
  const resetSelectedVault = useVaultSelectionStore(
    (s) => s.resetSelectedVault,
  );
  const onDepositOpen = useDepositDrawer((s) => s.onOpen);
  const onOpenDepositUpgradeDrawer = useDepositUpgradeDrawer((s) => s.onOpen);

  if (!selectedVault) {
    return null;
  }

  return (
    <Box>
      <Text textStyle="h2" textAlign="center">
        {formatLockPeriod(selectedVault.lockPeriod, { roughly: true })}{' '}
        {selectedVault.type === 'RESERVOIR' ? 'Reservoir' : 'Fountain'}
      </Text>
      <Card
        position="relative"
        w="564px"
        mx="auto"
        mt="32px"
        pb="32px"
        px={8}
        pt={4}
      >
        <Button
          variant="link"
          color="text-link"
          position="absolute"
          left="16px"
          top="30px"
          leftIcon={<Icon as={ArrowBack} boxSize="15px" />}
          onClick={resetSelectedVault}
        >
          {t('Back to {page}', {
            page: selectedVault.type === 'RESERVOIR' ? 'Reservoir' : 'Fountain',
          })}
        </Button>
        <VaultDetail />
        <Box
          mt={9}
          w="full"
          textAlign={!selectedVault.stakeCount ? 'center' : 'right'}
        >
          {!!selectedVault.stakeCount && selectedVault.pid < MAX_POOL_ID && (
            <Button
              variant="secondary"
              onClick={onOpenDepositUpgradeDrawer}
              textAlign="center"
              mx={4}
            >
              {t('Upgrade locking period')}
            </Button>
          )}

          <Button
            onClick={() => onDepositOpen()}
            w={!selectedVault.stakeCount ? MAXW_DESKTOP_CTA : undefined}
          >
            {selectedVault.stakeCount
              ? t('Deposit more')
              : t('Deposit in {platform}', {
                  platform:
                    selectedVault.type === 'RESERVOIR'
                      ? 'Reservoir'
                      : 'Fountain',
                })}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

const useVnoAndGarden = () => {
  useDefaultAppBar();
  const { pathname } = useRouter();
  const realPathname = useRealPathname();
  const openGardenModal = useGardenModalStore((s) => s.open);
  const openVaultDrawer = useVaultDetailDrawer((s) => s.onOpen);

  useMount(() => {
    const lastUrlParts = pathname.split('/').slice(-3);
    const pathInfo = getPathInfo(realPathname);

    if (lastUrlParts.includes('reservoir'))
      // timeout 0 because vault detail drawer store is reset on route change, and if we come from another route,
      // it could be directly closed again due to the reset. so this means the drawer will be opened after other side effects are complete.
      setTimeout(() => openVaultDrawer('RESERVOIR'), 0);
    else if (lastUrlParts.includes('fountain'))
      setTimeout(() => openVaultDrawer('FOUNTAIN'), 0);
    else if (pathInfo.category === 'garden') {
      openGardenModal({
        coinMode: pathInfo.coinMode,
        type: pathInfo.gardenType,
        activeView: pathInfo.gardenView,
      });
    }
  });
};
